import { Box, ListItemButton, ListItemIcon, Typography } from '@mui/material'
import { ReactNode } from 'react'
import { exhaustiveCheck } from '~pages/pages-behind-login/financials/earnings/utils/format-date-by-aggregate'
import { COLORS } from '~theme/colors'
import { LeftNavigationVariant, useNavigation } from '../../navigation-context'

interface MenuItemLayoutProps {
  icon: ReactNode
  label: string
  isActive: boolean
  suffixElement?: ReactNode
  onClick?: () => void
  forceHoverState?: boolean
  className?: string
}

interface ConstructColorsParams {
  isActive: boolean
  variant: LeftNavigationVariant
}

function getDefaultColor({ variant, isActive }: ConstructColorsParams) {
  switch (variant) {
    case 'primary':
      return isActive ? COLORS.primary.white : COLORS.secondary.darkBlue
    case 'secondary':
      return isActive ? COLORS.primary.white : COLORS.greys.dark
    default: {
      exhaustiveCheck(variant)
    }
  }
}

function getHoverColor({ variant, isActive }: ConstructColorsParams) {
  switch (variant) {
    case 'primary':
      return isActive ? COLORS.secondary.lightBlue : COLORS.secondary.darkBlue
    case 'secondary':
      return isActive ? COLORS.secondary.lightBlue : COLORS.greys.dark
    default: {
      exhaustiveCheck(variant)
    }
  }
}

function getDefaultBackgroundColor({
  variant,
  isActive,
}: ConstructColorsParams) {
  switch (variant) {
    case 'primary':
      return isActive ? COLORS.secondary.darkBlue : 'transparent'
    case 'secondary':
      return isActive ? COLORS.greys.dark : 'transparent'
    default: {
      exhaustiveCheck(variant)
    }
  }
}

function getHoverBackgroundColor({ variant, isActive }: ConstructColorsParams) {
  switch (variant) {
    case 'primary':
      return isActive ? COLORS.secondary.darkBlue : COLORS.secondary.blueTint
    case 'secondary':
      return isActive ? COLORS.greys.dark : COLORS.secondary.blueTint
    default: {
      exhaustiveCheck(variant)
    }
  }
}

export function MenuItemLayout({
  icon,
  label,
  isActive,
  suffixElement,
  onClick,
  forceHoverState,
  className,
}: MenuItemLayoutProps) {
  const { isOpen: isMenuOpen, navigationVariant: variant } = useNavigation()

  const hoverSx = {
    color: getHoverColor({ variant, isActive }),
    backgroundColor: getHoverBackgroundColor({ variant, isActive }),
    '& svg, span': {
      color: getHoverColor({ variant, isActive }),
    },
  }

  return (
    <ListItemButton
      className={className}
      onClick={onClick}
      sx={[
        {
          px: 2.5,
          minHeight: 48,
          display: 'flex',
          flexDirection: isMenuOpen ? 'row' : 'column',
          justifyContent: isMenuOpen ? 'initial' : 'center',
          mx: 2,
          borderRadius: 1,
          ...(forceHoverState
            ? hoverSx
            : {
                color: getDefaultColor({ variant, isActive }),
                backgroundColor: getDefaultBackgroundColor({
                  variant,
                  isActive,
                }),
              }),
          '&:hover': {
            color: getHoverColor({ variant, isActive }),
            backgroundColor: getHoverBackgroundColor({ variant, isActive }),
            '& svg, span': {
              color: getHoverColor({ variant, isActive }),
            },
          },
        },
      ]}
    >
      {icon && (
        <ListItemIcon
          sx={{
            minWidth: 0,
            justifyContent: 'center',
            mr: isMenuOpen ? 3 : 0,
            color: getDefaultColor({ variant, isActive }),
            transition: 'color 0.2s',
            '& svg': { width: '24px', height: '24px' },
          }}
        >
          {icon}
        </ListItemIcon>
      )}
      {label && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: isMenuOpen ? 'space-between' : 'center',
          }}
        >
          <Typography
            variant={isMenuOpen ? 'menuBold' : 'graphCaption'}
            sx={[
              isMenuOpen ? { mt: 0 } : { fontSize: '8px', mt: icon ? 1 : 0 },
              {
                whiteSpace: 'break-spaces',
                textAlign: 'center',
                color: getDefaultColor({ variant, isActive }),
              },
            ]}
          >
            {label}
          </Typography>
          {suffixElement}
        </Box>
      )}
    </ListItemButton>
  )
}
